import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Search, MapPin, Loader2 } from 'lucide-react';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  importance: number;
}

interface SearchBarProps {
  onSelect: (lng: number, lat: number, zoom: number) => void;
}

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

export const SearchBar: React.FC<SearchBarProps> = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [searchError, setSearchError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const performSearch = useCallback(async (q: string) => {
    if (!q.trim() || q.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    abortRef.current?.abort();
    abortRef.current = new AbortController();

    try {
      const params = new URLSearchParams({
        q: q.trim(),
        format: 'json',
        limit: '5',
        'accept-language': 'en',
      });
      const resp = await fetch(`${NOMINATIM_URL}?${params}`, {
        signal: abortRef.current.signal,
        headers: {
          'User-Agent': 'Cartografika/0.1.0 (research@stratosfyre.com)',
        },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data: NominatimResult[] = await resp.json();
      setResults(data);
      setSearchError(null);
      setHighlightedIndex(0);
      setIsOpen(data.length > 0);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        console.warn('Nominatim search failed:', err);
        setSearchError('Search unavailable — check connection');
      }
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.slice(0, 100);
    setQuery(val);
    if (searchError) setSearchError(null);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => performSearch(val), 300);
  };

  const handleSelect = (result: NominatimResult) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    onSelect(lng, lat, 16);
    setQuery(result.display_name);
    setIsOpen(false);
    setResults([]);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => (i - 1 + results.length) % results.length);
        break;
      case 'Enter':
        e.preventDefault();
        handleSelect(results[highlightedIndex]);
        break;
      case 'Escape':
        setIsOpen(false);
        inputRef.current?.blur();
        break;
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-search-bar]')) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return (
    <div data-search-bar className="relative w-full">
      <div className="flex items-center gap-2 rounded-lg bg-slate-800/90 px-3 py-2 backdrop-blur-sm border border-slate-600/50 shadow-lg">
        <Search className="h-4 w-4 shrink-0 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
          placeholder="Search any address or place…"
          className="flex-1 bg-transparent text-sm text-white placeholder-slate-400 outline-none min-w-0"
          autoComplete="off"
          aria-label="Search address"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        {isLoading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />}
        {searchError && !isLoading && (
          <span className="text-[10px] text-amber-400 shrink-0" aria-live="polite">Offline</span>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-auto rounded-lg border border-slate-600/50 bg-slate-800/95 shadow-xl backdrop-blur-sm"
        >
          {results.map((result, index) => (
            <li
              key={result.place_id}
              role="option"
              aria-selected={index === highlightedIndex}
              onMouseEnter={() => setHighlightedIndex(index)}
              onClick={() => handleSelect(result)}
              className={[
                'flex cursor-pointer items-center gap-2 px-3 py-2.5 text-sm transition-colors',
                index === highlightedIndex
                  ? 'bg-sky-900/30 text-sky-300'
                  : 'text-slate-300 hover:bg-slate-700/50',
              ].join(' ')}
            >
              <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-500" />
              <span className="truncate">{result.display_name}</span>
            </li>
          ))}
        </ul>
      )}
      {searchError && !isOpen && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border border-amber-700/30 bg-amber-900/80 px-3 py-2 text-xs text-amber-200 backdrop-blur-sm">
          {searchError}
        </div>
      )}
    </div>
  );
};
